import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Icon } from '@iwoca/orion';
import { PhoneSVG } from '../../../../svgComponents/phoneSVG/PhoneSVG';
import BenefitsCards from '../../../ui/benefitsCards/BenefitsCards';
import DashboardApprovedHeader from '../../../ui/DashboardApprovedHeader/DashboardApprovedHeader';
import { MainSectionWrapper } from '../../../pageStructure/mainSectionWrapper/MainSectionWrapper';
import { MoneyNoDecimals } from '../../../ui/moneyNoDecimals/MoneyNoDecimals';
import CustomiseOffer from './CustomiseOffer';
import RevenueStreamHeader from './RevenueStreamHeader';
import approvedViewStyles from '../flexiApprovedView/FlexiApprovedView.module.css';
import mainSectionWrapperStyles from '../../../pageStructure/mainSectionWrapper/MainSectionWrapper.module.css';
import messages from './RBLApprovedView.messages';
import styles from './RBLApprovedView.module.css';
import pendingStyles from '../../../pageStructure/mainContentPending/MainContentPending.module.css';
import { PaymentCardSVG } from '../../../../svgComponents/paymentCardSVG/PaymentCardSVG';
import { RootState } from '@iwProvider/configureStore';
import { useQuery } from '@tanstack/react-query';
import { fetchGetFormalOffers } from '@iwoca/lapi-client/edge';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import MainContentPending from '../../../pageStructure/mainContentPending';
import { getMinimumRepaymentIntervalAmount } from '@api/lendingAPI/rblCalculatorSelector';
import { TitleSectionWrapper } from '../flexiApprovedView/titleSectionWrapper/TitleSectionWrapper';

const mapStateToProps = (state: RootState) => {
    return {
        stateKey:
            state.siteContext.impersonatingStateKey ||
            state.siteContext.state_key,
        minimumRepaymentIntervalAmount: getMinimumRepaymentIntervalAmount(state)
    };
};

const BENEFITS = (minimumRepaymentIntervalAmount: number) => [
    {
        id: 'cover-costs',

        icon: <PaymentCardSVG className={styles.iconPaymentCardSVG} />,
        title: <FormattedMessage {...messages.cardOneTitle} />,
        content: (
            <FormattedMessage
                {...messages.cardOneContent}
                values={{ interval: minimumRepaymentIntervalAmount }}
            />
        )
    },
    {
        id: 'open-banking',
        icon: <Icon icon="beach" className={styles.iconPaymentCardSVG} />,
        title: <FormattedMessage {...messages.cardTwoTitle} />,
        content: <FormattedMessage {...messages.cardTwoContent} />
    },
    {
        id: 'personal-am',
        icon: <PhoneSVG className={styles.iconPhoneSVG} />,
        title: <FormattedMessage {...messages.cardThreeTitle} />,
        content: <FormattedMessage {...messages.cardThreeContent} />
    }
];

type PropsFromRedux = ReturnType<typeof mapStateToProps>;

const RBLApprovedView = ({
    stateKey,
    minimumRepaymentIntervalAmount
}: PropsFromRedux) => {
    const {
        isLoading,
        isError,
        data: getFormalOffersResponse
    } = useQuery({
        queryKey: ['formal_offers', stateKey],
        queryFn: () => fetchGetFormalOffers({ stateKey: stateKey! })
    });

    if (isLoading) {
        return (
            <MainContentPending data-testid="rbl-approval-content-loading" />
        );
    }

    if (isError) {
        return <FallbackErrorModal />;
    }
    const formalOffer = getFormalOffersResponse.data!.formal_offers[0];

    const availableCash = formalOffer.available_cash!;

    //temporarily force as true- RQ will be used to fetch this data and there will be a loading state. Currently there is no loading state.
    const initialCalculatorLoaded = true;
    return (
        <>
            <TitleSectionWrapper>
                <DashboardApprovedHeader
                    offer={
                        <>
                            <div
                                className={approvedViewStyles.headerPrice}
                                data-testid="header-price"
                            >
                                <FormattedMessage {...messages.borrowUpTo} />{' '}
                                <span className={approvedViewStyles.headerBlue}>
                                    <MoneyNoDecimals value={availableCash} />
                                </span>
                            </div>
                            <div className={styles.headerSecondLine}>
                                <FormattedMessage {...messages.withRBL} />
                            </div>
                        </>
                    }
                    offerInfo={<RevenueStreamHeader />}
                />
            </TitleSectionWrapper>

            <MainSectionWrapper
                className={approvedViewStyles.MainSection}
                innerWrapperClassName={mainSectionWrapperStyles.innerWrapper}
            >
                <div className={styles.container}>
                    <div>
                        <div className={styles.approvalSection}>
                            {initialCalculatorLoaded ? (
                                <CustomiseOffer />
                            ) : (
                                <div className={styles.customiseOfferLoading}>
                                    <div
                                        data-testid="customise-offer-loading"
                                        className={
                                            pendingStyles.longPendingItem
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={styles.benefits}>
                        <BenefitsCards
                            benefits={BENEFITS(minimumRepaymentIntervalAmount)}
                            source="RBL Approved View"
                        />
                    </div>
                </div>
            </MainSectionWrapper>
        </>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps)(RBLApprovedView);
